
import React, { useState, useEffect } from 'react';
import { researchCompany, updateVapiAssistant, sendContract, sendStripePaymentLink } from './api';
import supabase from '../supabase';

const s = {
  page: { padding: '40px', backgroundColor: '#f0ebe3', minHeight: '100vh', fontFamily: 'Arial, sans-serif' },
  card: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  h1: { color: '#6B1A1A', marginBottom: '8px', fontSize: '26px', fontWeight: 'bold' },
  h2: { color: '#6B1A1A', marginBottom: '20px' },
  input: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', width: '100%', boxSizing: 'border-box' },
  select: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', width: '100%', boxSizing: 'border-box' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' },
  label: { display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' },
  btnPrimary: { padding: '15px 30px', backgroundColor: '#6B1A1A', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' },
  btnGreen: { padding: '15px 30px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' },
  btnGray: { padding: '12px 24px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' },
  btnDisabled: { padding: '15px 30px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'not-allowed' },
};

const DemoBuilder = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [step, setStep] = useState('form');
  const [businessInfo, setBusinessInfo] = useState({ name: '', city: '', state: '' });
  const [tradeType, setTradeType] = useState('');
  const [handlesEmergencies, setHandlesEmergencies] = useState('');
  const [currentPhoneIssue, setCurrentPhoneIssue] = useState('');
  const [researchData, setResearchData] = useState(null);
  const [vapiAssistant, setVapiAssistant] = useState(null);
  const [isResearching, setIsResearching] = useState(false);
  const [isCreatingAssistant, setIsCreatingAssistant] = useState(false);
  const [isSendingContract, setIsSendingContract] = useState(false);
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [contractSent, setContractSent] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [prospectInfo, setProspectInfo] = useState({ primaryContact: '', email: '', phone: '', address: '' });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        window.location.href = '/login';
      } else {
      setAuthChecked(true);
        supabase.from('profiles').select('*').eq('email', session.user.email).single().then(({ data: profile }) => {
          setCurrentUser(profile);
        });
      }
    });
  }, []);

  // Restore state from localStorage on refresh
  useEffect(() => {
    const saved = localStorage.getItem('hermesDemo');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.step === 'sent' && parsed.contractSent) {
          setStep(parsed.step);
          setContractSent(parsed.contractSent);
          setBusinessInfo(parsed.businessInfo);
          setProspectInfo(parsed.prospectInfo);
          setResearchData(parsed.researchData);
         if (parsed.contractSigned) {
  console.log('Restoring contractSigned: true');
  setContractSigned(true);
}
          if (parsed.paymentSent) setPaymentSent(true);
        }
      } catch {}
    }
  }, []);

  // Poll for contract signature status
  useEffect(() => {
    if (step !== 'sent' || !contractSent || contractSigned) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`https://hermes-backend-mu.vercel.app/api/contract/${contractSent.contractId}`);
        const data = await res.json();
        if (data.success && data.contract.status === 'signed') {
          setContractSigned(true);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [step, contractSent, contractSigned]);
  useEffect(() => {
    if (contractSigned) {
      const savedData = JSON.parse(localStorage.getItem('hermesDemo') || '{}');
      localStorage.setItem('hermesDemo', JSON.stringify({...savedData, contractSigned: true}));
    }
  }, [contractSigned]);
  const handleResearch = async () => {
    if (!businessInfo.name || !businessInfo.city || !tradeType) {
      alert('Please enter business name, city, and select trade type');
      return;
    }
    setIsResearching(true);
    try {
      const research = await researchCompany(
        businessInfo.name, businessInfo.city, businessInfo.state,
        tradeType, { handlesEmergencies, currentPhoneIssue }
      );
      setResearchData(research);
      setStep('research');
    } catch (error) {
      alert('Research failed. Please try again.');
    }
    setIsResearching(false);
  };

  const handleCreateAssistant = async () => {
    setIsCreatingAssistant(true);
    try {
      const vapiResult = await updateVapiAssistant(businessInfo, researchData, handlesEmergencies, currentPhoneIssue);
      if (vapiResult.success) {
        setVapiAssistant(vapiResult.assistant);
        setStep('demo');
      } else throw new Error(vapiResult.error);
    } catch (error) {
      setVapiAssistant({
        phoneNumber: '+1 (985) 241 3228',
        name: `${businessInfo.name} Demo Assistant`,
        businessType: researchData.businessType,
        location: `${businessInfo.city}, ${businessInfo.state}`,
        customized: false, ready: true
      });
      setStep('demo');
    }
    setIsCreatingAssistant(false);
  };

  const handleSendContract = async () => {
    console.log('CURRENT USER:', currentUser);
    if (!prospectInfo.primaryContact || !prospectInfo.email || !prospectInfo.phone || !prospectInfo.address) {
      alert('Please fill in all prospect information');
      return;
    }
    setIsSendingContract(true);
    try {
      const result = await sendContract(
        { ...prospectInfo, businessName: businessInfo.name },
        businessInfo, researchData, currentUser?.full_name || 'Randy'
      );
      if (result.success) {
       setContractSent(result);
        setStep('sent');
        localStorage.setItem('hermesDemo', JSON.stringify({
          step: 'sent',
          contractSent: result,
          businessInfo,
          prospectInfo,
          researchData,
          contractSigned: false,
          paymentSent: false
        }));
      } else throw new Error(result.error);
    } catch (error) {
      alert(`Failed to send contract: ${error.message}`);
    }
    setIsSendingContract(false);
  };
    
  const handleSendPaymentLink = async () => {
    setIsSendingPayment(true);
    try {
      const result = await sendStripePaymentLink(prospectInfo, businessInfo);
      if (result.success) {
       setPaymentSent(true);
        const savedData = JSON.parse(localStorage.getItem('hermesDemo') || '{}');
        localStorage.setItem('hermesDemo', JSON.stringify({...savedData, paymentSent: true}));
      } else throw new Error(result.error);
    } catch (error) {
      alert(`Failed to send payment link: ${error.message}`);
    }
    setIsSendingPayment(false);
  };

  const reset = () => {
    localStorage.removeItem('hermesDemo');
    setStep('form');
    setBusinessInfo({ name: '', city: '', state: '' });
    setTradeType('');
    setHandlesEmergencies('');
    setCurrentPhoneIssue('');
    setResearchData(null);
    setVapiAssistant(null);
    setContractSent(null);
    setContractSigned(false);
    setPaymentSent(false);
    setProspectInfo({ primaryContact: '', email: '', phone: '', address: '' });
  };

  const stepKeys = ['form', 'research', 'demo', 'value', 'prospect', 'sent'];
  const stepIdx = stepKeys.indexOf(step);

  if (!authChecked) return null;
  
  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={s.h1}>🦅 Hermes Partner Capital</h1>
        <p style={{ color: '#999', margin: 0, fontSize: '13px' }}>Sales Platform</p>
      </div>

      {/* Progress Bar */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '30px' }}>
        {stepKeys.map((_, i) => (
          <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: i <= stepIdx ? '#6B1A1A' : '#ddd', transition: 'background 0.3s' }} />
        ))}
      </div>

      {/* ── FORM ─────────────────────────────────────────────────────────────── */}
      {step === 'form' && (
        <div style={s.card}>
          <h2 style={s.h2}>Business Information</h2>
          <div style={s.grid2}>
            <div>
              <label style={s.label}>Business Name *</label>
              <input style={s.input} placeholder="e.g. Johnson Plumbing" value={businessInfo.name}
                onChange={e => setBusinessInfo({...businessInfo, name: e.target.value})} />
            </div>
            <div>
              <label style={s.label}>City *</label>
              <input style={s.input} placeholder="e.g. Dallas" value={businessInfo.city}
                onChange={e => setBusinessInfo({...businessInfo, city: e.target.value})} />
            </div>
          </div>
          <div style={s.grid2}>
            <div>
              <label style={s.label}>State</label>
              <input style={s.input} placeholder="e.g. Texas" value={businessInfo.state}
                onChange={e => setBusinessInfo({...businessInfo, state: e.target.value})} />
            </div>
            <div>
              <label style={s.label}>Trade Type *</label>
              <select style={s.select} value={tradeType} onChange={e => setTradeType(e.target.value)}>
                <option value="">Select Trade Type</option>
                <option value="plumbing">Plumbing</option>
                <option value="HVAC">HVAC</option>
                <option value="roofing">Roofing</option>
                <option value="electrical">Electrical</option>
                <option value="property management">Property Management</option>
                <option value="landscaping">Landscaping</option>
                <option value="cleaning">Cleaning Services</option>
                <option value="pest control">Pest Control</option>
                <option value="painting">Painting</option>
                <option value="flooring">Flooring</option>
                <option value="handyman">Handyman Services</option>
                <option value="tree service">Tree Service</option>
                <option value="concrete">Concrete</option>
                <option value="fencing">Fencing</option>
                <option value="garage doors">Garage Doors</option>
                <option value="windows">Windows</option>
                <option value="solar">Solar</option>
                <option value="pool service">Pool Service</option>
                <option value="locksmith">Locksmith</option>
                <option value="septic">Septic</option>
                <option value="security systems">Security Systems</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={s.label}>Do they handle emergency calls?</label>
            <select style={s.select} value={handlesEmergencies} onChange={e => setHandlesEmergencies(e.target.value)}>
              <option value="">Select...</option>
              <option value="yes_24_7">Yes — 24/7 emergency service</option>
              <option value="yes_business_hours">Yes — during business hours only</option>
              <option value="sometimes">Sometimes — depends on availability</option>
              <option value="no">No — no emergency services</option>
            </select>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <label style={s.label}>Biggest phone challenge?</label>
            <select style={s.select} value={currentPhoneIssue} onChange={e => setCurrentPhoneIssue(e.target.value)}>
              <option value="">Select...</option>
              <option value="miss_calls_during_jobs">Miss calls when out on jobs</option>
              <option value="no_after_hours">No after-hours coverage</option>
              <option value="overwhelmed_peak_times">Overwhelmed during peak times</option>
              <option value="customers_hang_up">Customers hang up on voicemail</option>
              <option value="competing_for_calls">Losing calls to competitors</option>
              <option value="scheduling_inefficient">Inefficient scheduling</option>
            </select>
          </div>
          <button style={isResearching ? s.btnDisabled : s.btnPrimary} onClick={handleResearch} disabled={isResearching}>
            {isResearching ? 'Building Profile...' : 'Build Business Profile'}
          </button>
        </div>
      )}

      {/* ── RESEARCH RESULTS ─────────────────────────────────────────────────── */}
      {step === 'research' && researchData && (
        <div style={s.card}>
          <h2 style={s.h2}>{businessInfo.name} — Business Profile</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
            <div>
              <p><strong>Business Type:</strong> {researchData.businessType}</p>
              <p><strong>Location:</strong> {businessInfo.city}, {businessInfo.state}</p>
              <p><strong>Phone Setup:</strong> {researchData.currentPhoneSetup}</p>
            </div>
            <div>
              <p><strong>Services:</strong></p>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {researchData.services.slice(0, 5).map((svc, i) => <li key={i}>{svc}</li>)}
              </ul>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
            <div style={{ backgroundColor: '#fff5f5', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ color: '#dc3545', margin: '0 0 10px 0' }}>Pain Points</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {researchData.painPoints.map((p, i) => <li key={i} style={{ marginBottom: '5px' }}>{p}</li>)}
              </ul>
            </div>
            <div style={{ backgroundColor: '#f0fff4', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ color: '#28a745', margin: '0 0 10px 0' }}>Opportunities</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {researchData.opportunities.map((o, i) => <li key={i} style={{ marginBottom: '5px' }}>{o}</li>)}
              </ul>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button style={isCreatingAssistant ? s.btnDisabled : s.btnGreen} onClick={handleCreateAssistant} disabled={isCreatingAssistant}>
              {isCreatingAssistant ? 'Building AI Receptionist...' : 'Create Live Demo'}
            </button>
            <button style={s.btnGray} onClick={reset}>New Prospect</button>
          </div>
        </div>
      )}

      {/* ── DEMO ─────────────────────────────────────────────────────────────── */}
      {step === 'demo' && vapiAssistant && (
        <div style={s.card}>
          <h2 style={s.h2}>Live Demo — {businessInfo.name}</h2>
          <div style={{ backgroundColor: '#28a745', color: 'white', padding: '30px', borderRadius: '12px', textAlign: 'center', marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>📞 {vapiAssistant.phoneNumber}</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '18px' }}>AI Receptionist Ready for {businessInfo.name}</p>
            <button onClick={() => window.open(`tel:${vapiAssistant.phoneNumber}`, '_self')}
              style={{ padding: '15px 30px', backgroundColor: 'white', color: '#28a745', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>
              Call Demo Number
            </button>
          </div>
          <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '25px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Suggested Questions</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>"Hi, I need emergency {researchData?.businessType} service"</li>
              <li style={{ marginBottom: '8px' }}>"What services do you offer?"</li>
              <li style={{ marginBottom: '8px' }}>"Can you schedule an appointment?"</li>
              <li style={{ marginBottom: '8px' }}>"What are your hours?"</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button style={s.btnPrimary} onClick={() => setStep('value')}>
              Continue
            </button>
            <button style={s.btnGray} onClick={reset}>New Prospect</button>
          </div>
        </div>
      )}

      {/* ── VALUE SUMMARY ────────────────────────────────────────────────────── */}
      {step === 'value' && (
        <div style={s.card}>
          <h2 style={s.h2}>What's Included for {businessInfo.name}</h2>
          <div style={{ backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '30px', marginBottom: '25px' }}>
            {[
              ['📞', '24/7 AI receptionist trained on your business', 'Never miss another call — day or night'],
              ['🎯', 'Lead qualification on every call', 'AI screens callers and collects job details automatically'],
              ['📅', 'Appointment booking', 'Scheduled directly into your calendar or preferred tool'],
              ['📲', 'Instant SMS alerts', 'You get notified the moment a qualified lead calls in'],
              ['🔄', 'Automated follow-up sequences', 'Unbooked leads get followed up so you never lose a job'],
              ['⚙️', 'Full setup & ongoing optimization', 'We handle everything — you just answer the jobs'],
            ].map(([icon, title, desc], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize: '24px', marginRight: '15px', minWidth: '32px' }}>{icon}</span>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#333' }}>{title}</p>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{desc}</p>
                </div>
                <span style={{ marginLeft: 'auto', color: '#28a745', fontSize: '20px' }}>✓</span>
              </div>
            ))}
          </div>
          <div style={{ backgroundColor: '#6B1A1A', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center', marginBottom: '25px' }}>
            <p style={{ margin: '0 0 5px 0', fontSize: '14px', opacity: 0.8 }}>Investment</p>
            <p style={{ margin: '0 0 5px 0', fontSize: '28px', fontWeight: 'bold' }}>$500 setup + $350/month</p>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>Month-to-month — cancel anytime with 30 days notice</p>
          </div>
          <button style={s.btnGreen} onClick={() => setStep('prospect')}>
            Proceed to Agreement
          </button>
        </div>
      )}

      {/* ── PROSPECT INFO ────────────────────────────────────────────────────── */}
      {step === 'prospect' && (
        <div style={s.card}>
          <h2 style={s.h2}>Contact Information</h2>
          <p style={{ color: '#666', marginBottom: '25px' }}>We'll send the service agreement directly to their inbox for review and signature.</p>
          <div style={s.grid2}>
            <div>
              <label style={s.label}>Primary Contact Name *</label>
              <input style={s.input} placeholder="e.g. John Smith"
                value={prospectInfo.primaryContact}
                onChange={e => setProspectInfo({...prospectInfo, primaryContact: e.target.value})} />
            </div>
            <div>
              <label style={s.label}>Email Address *</label>
              <input style={s.input} type="email" placeholder="e.g. john@johnsonplumbing.com"
                value={prospectInfo.email}
                onChange={e => setProspectInfo({...prospectInfo, email: e.target.value})} />
            </div>
          </div>
          <div style={s.grid2}>
            <div>
              <label style={s.label}>Phone Number *</label>
              <input style={s.input} placeholder="e.g. (214) 555-0123"
                value={prospectInfo.phone}
                onChange={e => setProspectInfo({...prospectInfo, phone: e.target.value})} />
            </div>
            <div>
              <label style={s.label}>Business Address *</label>
              <input style={s.input} placeholder="e.g. 123 Main St, Dallas, TX 75001"
                value={prospectInfo.address}
                onChange={e => setProspectInfo({...prospectInfo, address: e.target.value})} />
            </div>
          </div>
          <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '25px' }}>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              Agreement will be sent to <strong>{prospectInfo.email || 'their email'}</strong> for electronic signature.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button style={isSendingContract ? s.btnDisabled : s.btnGreen} onClick={handleSendContract} disabled={isSendingContract}>
              {isSendingContract ? 'Sending Agreement...' : 'Send Agreement'}
            </button>
            <button style={s.btnGray} onClick={() => setStep('value')}>Back</button>
          </div>
        </div>
      )}

      {/* ── SENT / ACTION CENTER ─────────────────────────────────────────────── */}
      {step === 'sent' && contractSent && (
        <div style={s.card}>
          <h2 style={s.h2}>{businessInfo.name} — Client Pipeline</h2>

          {/* Contract Status */}
          <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#999' }}>Contract ID</p>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{contractSent.contractId}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#999' }}>Sent to</p>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{prospectInfo.email}</p>
              </div>
            </div>
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
              <span style={{
                padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold',
                backgroundColor: contractSigned ? '#d4edda' : '#fff3cd',
                color: contractSigned ? '#155724' : '#856404'
              }}>
                {contractSigned ? 'Signed' : 'Awaiting Signature'}
              </span>
              {!contractSigned && <span style={{ fontSize: '12px', color: '#999', marginLeft: '10px' }}>Checking automatically...</span>}
            </div>
          </div>

          {/* Action Checklist — internal only */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontWeight: 'bold', color: '#333', marginBottom: '15px' }}>Next Steps</p>

            {/* Step 1 — Contract */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '15px', backgroundColor: contractSigned ? '#d4edda' : '#f8f9fa', borderRadius: '8px', marginBottom: '10px', border: `1px solid ${contractSigned ? '#c3e6cb' : '#eee'}` }}>
              <span style={{ fontSize: '20px', marginRight: '15px' }}>{contractSigned ? '✓' : '○'}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>Service Agreement</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{contractSigned ? `Signed by ${prospectInfo.primaryContact}` : 'Sent — waiting for signature'}</p>
              </div>
            </div>

            {/* Step 2 — Payment */}
            <div style={{ padding: '15px', backgroundColor: paymentSent ? '#d4edda' : '#f8f9fa', borderRadius: '8px', marginBottom: '10px', border: `1px solid ${paymentSent ? '#c3e6cb' : '#eee'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '20px', marginRight: '15px' }}>{paymentSent ? '✓' : '○'}</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>Setup Payment — $500</p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{paymentSent ? 'Payment link sent' : 'Send Stripe payment link to client'}</p>
                  </div>
                </div>
                {!paymentSent && (
                  <button
                    style={{ ...s.btnPrimary, padding: '10px 20px', fontSize: '14px', opacity: contractSigned ? 1 : 0.5, cursor: contractSigned ? 'pointer' : 'not-allowed' }}
                    onClick={contractSigned ? handleSendPaymentLink : undefined}
                    disabled={!contractSigned || isSendingPayment}
                  >
                    {isSendingPayment ? 'Sending...' : 'Send Payment Link'}
                  </button>
                )}
              </div>
            </div>

            {/* Step 3 — Onboarding */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '10px', border: '1px solid #eee', opacity: paymentSent ? 1 : 0.5 }}>
              <span style={{ fontSize: '20px', marginRight: '15px' }}>○</span>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>Onboarding Form</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Send once payment is confirmed</p>
              </div>
            </div>

            {/* Step 4 — Build */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', opacity: 0.5 }}>
              <span style={{ fontSize: '20px', marginRight: '15px' }}>○</span>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>Build & Launch AI Receptionist</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Configure and go live within 5 business days</p>
              </div>
            </div>
          </div>

          <button style={s.btnGray} onClick={reset}>Start New Demo</button>
        </div>
      )}
    </div>
  );
};

export default DemoBuilder;